'use client';

import * as React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';
import { editorBandSurface } from '@/lib/editorial';
import { HeroRequestBar } from '@/components/sections/HeroRequestBar';


const INK = '#18191B';
const CREAM = '#f5f2ed';
const CREAM2 = '#ece7dc';
const GOLD = '#daba7b';
const GOLD_DEEP = '#b9a273';
const MUTED = '#6b6a64';
const INK_SOFT = '#2a2b2e';
const RULE = 'rgba(24,25,27,0.18)';
const RULE_SOFT = 'rgba(24,25,27,0.08)';

const pillars = [
  {
    idx: '01', title: 'Hotel Booking Platform',
    img: '/pillar-picture-1.png',
    href: '/hotels',
    memberHref: '/membership',
    bullets: ['Global platform for exclusive hotels and villas.', 'Get our privileged rates and benefits.'],
  },
  {
    idx: '02', title: 'Curated Experiences',
    img: '/pillar-picture-2.png',
    href: '/initiatives',
    memberHref: '/membership',
    bullets: ['We regularly publish exclusive new initiatives.', 'VIP offers curated for each season.'],
  },
  {
    idx: '03', title: 'Concierge Services',
    img: '/pillar-picture-3.png',
    href: '/services',
    memberHref: '/services',
    bullets: ['We help you acquire exclusive products, services and experiences.', 'Everything is sold at market price.'],
  },
];

const whatWeDo = [
  { num: 'I.', title: 'One Point of Contact', body: 'All products, services and experiences are conveniently gathered in one place.' },
  { num: 'II.', title: 'Market Price', body: 'All products, services and experiences are sold at market price.' },
  { num: 'III.', title: 'Local and Global Presence', body: 'Through our extensive network, our services are available both nationally and internationally.' },
];

const destinations = [
  { name: 'French Riviera', country: 'France', img: '/locations/FRENCH RIVIERA.webp' },
  { name: 'Stockholm', country: 'Sweden', img: '/locations/STOCKHOLM.webp' },
  { name: 'Ibiza', country: 'Spain', img: '/locations/IBIZA.webp' },
  { name: 'Copenhagen', country: 'Denmark', img: '/locations/Copenhagen.webp' },
  { name: 'Reykjavík', country: 'Iceland', img: '/locations/Reykjavik.webp' },
  { name: 'Portugal', country: 'Lisbon · Algarve', img: '/locations/Portugal.webp' },
];

const concierge = [
  { name: 'Accommodation', img: '/services/accomodation.webp' },
  { name: 'Gastronomy', img: '/services/gastronomy.webp' },
  { name: 'Private Aviation', img: '/services/aviaions.webp' },
  { name: 'Boats & Yachts', img: '/services/boatandyact.webp' },
  { name: 'Bespoke Travel', img: '/services/bespoke-travelling.webp' },
  { name: 'Luxury Cars', img: '/services/luxury-rental-cars.webp' },
];

export default function HomePage() {
  const [currentUser, setCurrentUser] = React.useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = React.useState(true);
  const [firstName, setFirstName] = React.useState<string | null>(null);
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setFirstName(user?.displayName?.split(' ')[0] || null);
      setIsAuthLoading(false);
      if (user) {
        try {
          const name = (await getDoc(doc(db, 'users', user.uid))).data()?.name;
          if (name) setFirstName(String(name).trim().split(/\s+/)[0]);
        } catch {
          // The greeting is cosmetic; fall back to the auth display name.
        }
      }
    });
    return () => unsubscribe();
  }, []);

  if (isAuthLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)', background: CREAM }}>
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: GOLD }} />
      </div>
    );
  }

  return (
    <div style={{ background: CREAM, color: INK, fontFamily: "'Halyard Display', 'Inter', system-ui, sans-serif" }}>

      {/* Hero */}
      <section className="e-hero">
        <Image
          src="/main-page-background.png"
          alt="Luxury background"
          fill
          priority
          quality={90}
          style={{ objectFit: 'cover', filter: 'brightness(0.78)' }}
        />
        {/*
          The scrim is what the headline is read against. It used to clear completely at 30% and
          only darken again at the very bottom, which is fine on a wide desktop crop but not on a
          phone: the hero is tall and narrow there, so the text lands in the middle — straight over
          the bright sky — and the gold all but disappeared. It now stays dark from the middle down.
        */}
        <div className="e-hero-scrim" style={{ position: 'absolute', inset: 0 }} />

        <div className="e-pad" style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', color: CREAM,
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontFamily: "'Halyard Display', system-ui, sans-serif",
          }}>

          </div>

          <div style={{ color: CREAM }}>
            {/*
              Only rendered when there is a name to show: an empty element still reserved 22px of
              margin and pushed the headline down for every signed-out visitor.
              The shadow is what makes 11px of gold legible where the sky behind it is brightest.
            */}
            {currentUser && firstName && (
              <div style={{
                fontFamily: "'Halyard Display', system-ui, sans-serif",
                fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
                marginBottom: 22, color: GOLD,
                textShadow: '0 1px 12px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.7)',
              }}>
                ⟶ Welcome back, {firstName}
              </div>
            )}
            <h1 style={{
              fontFamily: "'Forum', serif",
              fontSize: 'clamp(56px, 7.5vw, 96px)', lineHeight: 0.95,
              fontWeight: 400, margin: 0, fontStyle: 'italic',
              // Gold on a bright sky is the weakest pairing on the page; the shadow keeps it
              // readable without darkening the photograph any further.
              textShadow: '0 2px 24px rgba(0,0,0,0.55)',
            }}>
              Your Lifestyle &<br />
              <span style={{ color: GOLD }}>Luxury Advisor.</span>
            </h1>
            {currentUser ? (
            // Request bar on the left, Browse Hotels at the far right — same layout as the logged-out hero.
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 36, gap: 24, flexWrap: 'wrap' }}>
              <HeroRequestBar user={currentUser} />
              <Link href="/hotels" className="w-full sm:w-auto text-center" style={{
                background: GOLD, color: INK, border: 'none',
                padding: '16px 28px', fontSize: 11, letterSpacing: '0.22em',
                textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none',
                fontFamily: "'Halyard Display', system-ui, sans-serif",
                display: 'inline-block', whiteSpace: 'nowrap',
              }}>Browse Hotels ⟶</Link>
            </div>
            ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 36, gap: 24, flexWrap: 'wrap' }}>
              <p style={{
                maxWidth: 520, fontSize: 14, lineHeight: 1.65,
                color: 'rgba(245,242,237,0.85)', margin: 0,
                fontFamily: "'Halyard Display', system-ui, sans-serif",
              }}>
                Experienced professionals with extensive knowledge of the luxury segment, specialised
                in the Nordic region. Privileged rates, curated experiences, and a single point of contact
                — at market price.
              </p>
              <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
                <Link href="/membership" className="w-full sm:w-auto text-center" style={{
                  background: GOLD, color: INK, border: 'none',
                  padding: '16px 28px', fontSize: 11, letterSpacing: '0.22em',
                  textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none',
                  fontFamily: "'Halyard Display', system-ui, sans-serif",
                  display: 'inline-block',
                }}>Browse Hotels ⟶</Link>
                <Link href="/membership" className="w-full sm:w-auto text-center" style={{
                  background: 'transparent', color: CREAM, border: '1px solid rgba(245,242,237,0.6)',
                  padding: '16px 28px', fontSize: 11, letterSpacing: '0.22em',
                  textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none',
                  fontFamily: "'Halyard Display', system-ui, sans-serif",
                  display: 'inline-block',
                }}>Become a Member</Link>
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* ① OUR SERVICES — three pillars */}
      <div className="e-pad" style={{ paddingBottom: 24 }}>
        <div style={{ marginBottom: 40 }}>
          <div style={{
            fontFamily: "'Halyard Display', system-ui, sans-serif",
            fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
            color: GOLD_DEEP, marginBottom: 12,
          }}>① · OUR SERVICES</div>
          <h2 style={{
            fontFamily: "'Forum', serif",
            fontSize: 'clamp(36px, 4.4vw, 56px)', fontWeight: 400, margin: 0, fontStyle: 'italic',
          }}>Three pillars, one address book.</h2>
        </div>

        <div className="e-grid-3">
          {pillars.map(p => (
            <Link
              key={p.idx}
              href={currentUser ? p.href : p.memberHref}
              style={{
                padding: 24, display: 'block', textDecoration: 'none', color: INK,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                <span style={{ fontFamily: "'Forum', serif", fontSize: 14, color: GOLD_DEEP }}>№ {p.idx}</span>
                <span style={{ fontFamily: "'Halyard Display', system-ui, sans-serif", fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: MUTED }}>For Members</span>
              </div>
              <div style={{ aspectRatio: '4/3', overflow: 'hidden', marginBottom: 20, position: 'relative' }}>
                <Image src={p.img} alt={p.title} fill style={{ objectFit: 'cover' }} />
              </div>
              <h3 style={{
                fontFamily: "'Forum', serif",
                fontSize: 28, fontWeight: 400, margin: 0, fontStyle: 'italic',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{p.title}</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', fontSize: 13, lineHeight: 1.7, color: INK_SOFT }}>
                {p.bullets.map(b => (
                  <li key={b} style={{ position: 'relative', paddingLeft: 18, marginBottom: 6 }}>
                    <span style={{ position: 'absolute', left: 0, top: 0, color: GOLD_DEEP }}>—</span>{b}
                  </li>
                ))}
              </ul>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: 20, paddingTop: 14, borderTop: `1px solid ${RULE_SOFT}`,
              }}>
                <div style={{
                  fontFamily: "'Halyard Display', system-ui, sans-serif",
                  fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: INK,
                }}>Open ⟶</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* ② WHAT WE DO */}
      <div className="e-pad" style={{ paddingBottom: 24 }}>
        <div style={{ marginBottom: 40 }}>
          <div style={{
            fontFamily: "'Halyard Display', system-ui, sans-serif",
            fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
            color: GOLD_DEEP, marginBottom: 12,
          }}>② · WHAT WE DO</div>
          <h2 style={{
            fontFamily: "'Forum', serif",
            fontSize: 'clamp(36px, 4.4vw, 56px)', fontWeight: 400, margin: 0, fontStyle: 'italic',
          }}>How we do it, in a sentence each.</h2>
        </div>

        <div className="e-grid-3-gap" style={{ borderTop: `1px solid ${INK}`, borderBottom: `1px solid ${INK}`, gap: 0 }}>
          {whatWeDo.map((w, i) => (
            <div key={w.num} style={{ padding: '40px 32px' }} className={i < 2 ? 'e-what-we-do-item' : ''}>
              <div style={{ fontFamily: "'Forum', serif", fontSize: 64, fontStyle: 'italic', color: GOLD_DEEP, lineHeight: 1, marginBottom: 16 }}>{w.num}</div>
              <h3 style={{
                fontFamily: "'Forum', serif",
                fontSize: 28, fontWeight: 400, margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{w.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.65, color: INK_SOFT, marginTop: 14, marginBottom: 0 }}>{w.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ④ CONCIERGE SERVICES */}
      <div className="e-pad" style={{ paddingBottom: 24 }}>
        <div style={{ marginBottom: 40 }}>
          <div style={{
            fontFamily: "'Halyard Display', system-ui, sans-serif",
            fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
            color: GOLD_DEEP, marginBottom: 12,
          }}>④ · ACCESS TO THE INACCESSIBLE</div>
          <h2 style={{
            fontFamily: "'Forum', serif",
            fontSize: 'clamp(36px, 4.4vw, 56px)', fontWeight: 400, margin: 0, fontStyle: 'italic',
          }}>What our concierge can arrange.</h2>
        </div>

        <div className="e-grid-6">
          {concierge.map(s => (
            <div key={s.name}>
              <div style={{ aspectRatio: '3/4', overflow: 'hidden', position: 'relative' }}>
                <Image src={s.img} alt={s.name} fill style={{ objectFit: 'cover' }} />
              </div>
              <div style={{
                fontFamily: "'Halyard Display', system-ui, sans-serif",
                fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
                marginTop: 10, paddingTop: 8, borderTop: `1px solid ${RULE}`,
              }}>{s.name}</div>
            </div>
          ))}
        </div>
        <div style={{
          fontFamily: "'Halyard Display', system-ui, sans-serif",
          fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
          marginTop: 24, textAlign: 'right',
        }}>
          <Link href="/services" style={{ borderBottom: `1px solid ${INK}`, paddingBottom: 2, textDecoration: 'none', color: INK }}>
            See all services →
          </Link>
        </div>
      </div>

      {/* EDITOR'S NOTE — About */}
      <div className="e-pad" style={{ ...editorBandSurface, marginTop: 56 }}>
        <div className="e-grid-1x2" style={{ gap: 56, alignItems: 'flex-start', maxWidth: 1280, margin: '0 auto' }}>
          <div>
            <div style={{
              fontFamily: "'Halyard Display', system-ui, sans-serif",
              fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: GOLD, marginTop: 24,
            }}>FROM THE EDITOR</div>
            <h3 style={{
              fontFamily: "'Forum', serif",
              fontSize: 40, fontStyle: 'italic', fontWeight: 400, margin: '12px 0 0', lineHeight: 1.05,
            }}>About Us</h3>
          </div>
          <div style={{ fontFamily: "'Forum', serif", fontSize: 22, fontStyle: 'italic', fontWeight: 300, lineHeight: 1.5, color: 'rgba(245,242,237,0.9)' }}>
            <p style={{ margin: 0 }}>
              Our team consists of experienced professionals with extensive knowledge of the
              luxury segment, specialised in the Nordic region. Through our burning passion
              for service and collaboration, we utilize our extensive network of global partners
              which empower us to <span style={{ color: GOLD }}>offer access to the inaccessible</span>.
            </p>
            <p style={{ marginTop: 24, marginBottom: 0 }}>
              We negotiate directly with top-tier suppliers to secure exclusive rates, allowing
              our members to enjoy market prices while we earn from the savings we negotiate.
              You always get the best <em>without paying extra</em>.
            </p>
            <div style={{
              fontFamily: "'Halyard Display', system-ui, sans-serif",
              fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: GOLD, marginTop: 32,
            }}>— Nordic LuxAdvisor · Oslo</div>
          </div>
        </div>
      </div>

    </div>
  );
}
